import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Icon, icons } from '../../generic';
import Flex from '../../generic/Flex';
import { Menu, MenuDivider, MenuHeader, MenuItem, SearchMenuItem } from '../../generic/Menu';
import type { Filter, ServiceFilter } from '../../../types';

import useIsGithubApp from './hooks/useIsGithubApp';
import { redirectToGithubAppInstallation } from './utils';

import styles from './ServiceFilterMenu.module.less';

type ServiceFilterProps = {
  categories: ServiceFilter[];
  onSelect: (filterType: string, filterValue: string) => void;
  selectedFilter?: Filter;
};

const ServiceProfileImage: VoidFunctionComponent<{ imageSrc?: string }> = ({ imageSrc }) => {
  return imageSrc ? (
    <img
      src={imageSrc}
      style={{
        height: 24,
        width: 24,
      }}
      alt=""
    />
  ) : (
    <Icon icon={icons.circleThin} />
  );
};

/**
 * Render a menu button that opens a dropdown menu to allow users to select different organizations and users.
 */
const ServiceFiltersMenu = ({ categories, selectedFilter, onSelect }: ServiceFilterProps) => {
  const [filter, setFilter] = useState('');
  const isGithubApp = useIsGithubApp();

  return (
    <Menu
      menuButton={
        <button type="button" className={styles.serviceFilterMenuButton}>
          <ServiceProfileImage imageSrc={selectedFilter?.icon} />
          {selectedFilter?.name}
          <Icon icon={icons.chevronDown} />
        </button>
      }
      direction="bottom"
    >
      <SearchMenuItem
        placeholder="Search..."
        onChange={(event) => {
          const searchInput = event.target.value;
          setFilter(searchInput);
        }}
      />
      {categories?.map((category) => {
        if (!category.filters?.length) {
          return null;
        }
        const currentFilters = category.filters?.filter((f) => !filter || f.name.toLowerCase().includes(filter)) ?? [];

        return (
          <>
            <MenuDivider />
            <MenuHeader>{category.category?.toUpperCase()}</MenuHeader>
            {!currentFilters.length ? (
              <MenuItem disabled>No results found</MenuItem>
            ) : (
              currentFilters.map((item) => {
                return (
                  <MenuItem key={item.value} onClick={() => onSelect(category.key, item.value)}>
                    <Flex gap="8px">
                      <ServiceProfileImage imageSrc={item.icon} />
                      {item.name}
                    </Flex>
                  </MenuItem>
                );
              })
            )}
          </>
        );
      })}
      {isGithubApp && (
        <>
          <MenuDivider />
          <MenuItem
            onClick={redirectToGithubAppInstallation}
          >
            Install GitHub App
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export default ServiceFiltersMenu;
